import { BillingCycle, Coupon, LicensePlan } from '@/common/types/organisation';
import {
  formatPrice,
  getCouponValid,
  getPlanBreakdown,
  PLAN_DETAILS,
} from '@/common/utils/license';
import tw from '@/common/utils/twmacro';
import { Button } from '@shadcn/components/button';
import { Separator } from '@shadcn/components/separator';

import { useMemo } from 'react';

interface PlanDetailsProps {
  plan: LicensePlan;
  seatCount: number;
  billingCycle: BillingCycle;
  currency: 'USD' | 'AUD' | 'NZD';
  coupon: Coupon | null;
  onEdit?: () => void;
  hideHeader?: boolean;
}
export default function PlanDetails({
  plan,
  seatCount,
  billingCycle,
  currency,
  coupon,
  onEdit,
  hideHeader = false,
}: PlanDetailsProps) {
  const planDetails = PLAN_DETAILS[plan];

  const couponValid = useMemo(
    () => getCouponValid(coupon?.appliedAt, billingCycle),
    [coupon, billingCycle],
  );

  const planBreakdown = useMemo(() => {
    return getPlanBreakdown(
      plan,
      seatCount,
      billingCycle,
      currency,
      coupon?.percentOff ?? null,
      couponValid,
    );
  }, [plan, seatCount, billingCycle, currency, coupon, couponValid]);

  const billingCycleLabel = useMemo(() => {
    switch (billingCycle) {
      case 'monthly':
        return 'month';
      case '1year':
        return 'year';
      case '2year':
        return '2 year';
      case '3year':
        return '3 year';
      default:
        return 'annual';
    }
  }, [billingCycle]);

  return (
    <PlanDetailsContainer>
      {!hideHeader && (
        <>
          <span className="font-medium text-base md:text-lg flex gap-2 items-end pt-2">
            <span className="leading-none">{planDetails.name} Plan</span>
            <span className="font-light text-sm leading-4 text-muted-foreground capitalize">
              {billingCycleLabel}ly
            </span>
            {onEdit && (
              <Button
                variant="link"
                size="sm"
                onClick={onEdit}
                className="ml-auto h-fit"
              >
                Edit
              </Button>
            )}
          </span>
          <Separator className="my-2" />
        </>
      )}
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col">
          <div className="flex justify-between">
            <PlanDetailsLabel className="flex flex-col">
              Base Price
            </PlanDetailsLabel>
            <span className="font-light">
              {formatPrice(planBreakdown.basePrice, currency)}
            </span>
          </div>
          <span className="font-light text-xs text-muted-foreground">
            Includes {planDetails.seatCount} learner accounts
          </span>
        </div>
        <div>
          <PlanDetailsLabel>Additional Learner Accounts</PlanDetailsLabel>
          <div className="flex justify-between font-light">
            <span>
              {planBreakdown.additionalSeats} x{' '}
              {formatPrice(planBreakdown.additionalSeatPrice, currency, false)}
            </span>
            <span>
              {formatPrice(
                planBreakdown.additionalSeats *
                  planBreakdown.additionalSeatPrice,
                currency,
              )}
            </span>
          </div>
        </div>
      </div>
      <Separator className="my-2 mt-auto" />
      <div className="flex justify-between">
        <PlanDetailsLabel>Subtotal</PlanDetailsLabel>
        <span className="font-light">
          {formatPrice(planBreakdown.subtotal, currency)}
        </span>
      </div>
      <Separator className="my-2" />
      <div className="flex justify-between">
        <PlanDetailsLabel>
          <div className="flex flex-col">
            Multi-Year Discount
            {planBreakdown.multiYearDiscountAmount !== 'N/A' && (
              <span className="font-light text-xs text-muted-foreground">
                {billingCycleLabel} plan -{' '}
                <span className="font-semibold">
                  {planBreakdown.multiYearDiscount}% off
                </span>
              </span>
            )}
          </div>
        </PlanDetailsLabel>
        <span className="font-light flex my-auto">
          {planBreakdown.multiYearDiscountAmount}
        </span>
      </div>
      {coupon && couponValid && planBreakdown.coupon && (
        <>
          <Separator className="my-2" />
          <div className="flex justify-between">
            <PlanDetailsLabel>
              <div className="flex flex-col">
                Discount Code
                <span className="font-semibold text-xs text-muted-foreground">
                  {coupon.code} - {planBreakdown.coupon.discount}% off
                </span>
              </div>
            </PlanDetailsLabel>
            <span className="font-light flex my-auto">
              {planBreakdown.coupon.discountAmount}
            </span>
          </div>
        </>
      )}
      <Separator className="my-2" />
      <div className="flex justify-between">
        <PlanDetailsLabel>Total</PlanDetailsLabel>
        <span className="font-light flex items-center gap-1">
          <span>{formatPrice(planBreakdown.total, currency)}</span>
          <span className="text-xs text-muted-foreground font-light">
            / {billingCycleLabel}
            {billingCycle === '2year' || billingCycle === '3year' ? 's' : ''}
          </span>
        </span>
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <PlanDetailsLabel>Price per learner</PlanDetailsLabel>
        <span className="font-light flex items-center gap-1">
          <span>
            {formatPrice(planBreakdown.pricePerSeat, currency)} /{' '}
            {billingCycle === 'monthly' ? 'month' : 'year'}
          </span>
        </span>
      </div>
    </PlanDetailsContainer>
  );
}

const PlanDetailsContainer = tw.div`flex flex-col min-w-[280px] h-full gap-2 bg-muted/50 rounded-md p-3 md:p-4 flex-1 text-sm box-border`;
const PlanDetailsLabel = tw.div`font-medium`;
